import type { App } from 'vue';
import * as Sentry from '@sentry/vue';
import type { Router } from 'vue-router';
import { CONFIG } from '@/app/config/env';

/** Initialise Sentry for Vue 3.
 *
 *  No-ops when `VITE_SENTRY_DSN` is empty — that's the default for local
 *  development. Wire the DSN in `.env.production` or via CI.
 *
 *  This must be called before any error can occur, i.e. right after
 *  `createApp(...)` and before `app.use(router)`. */
export function initSentry(app: App, router: Router): void {
  const dsn = CONFIG.SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    app,
    dsn,
    environment: CONFIG.SENTRY_ENVIRONMENT,
    release: CONFIG.SENTRY_RELEASE || undefined,
    sampleRate: clampFraction(CONFIG.SENTRY_SAMPLE_RATE, 1),
    tracesSampleRate: clampFraction(CONFIG.SENTRY_TRACES_SAMPLE_RATE, 0),
    // Vue → Sentry integration handles component render errors + lifecycle
    // hook exceptions. The SDK mirrors them to the console by default.
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Scrub obvious credentials before send.
    beforeSend(event) {
      return scrubEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      // Redact bearer tokens from any HTTP breadcrumb data.
      const data = breadcrumb.data as Record<string, unknown> | undefined;
      if (data && typeof data['Authorization'] === 'string') {
        data['Authorization'] = 'Bearer [redacted]';
      }
      return breadcrumb;
    },
  });
}

/** Attach the currently authenticated tenant admin (or super-admin) to
 *  Sentry so errors show who was affected. Call after auth hydration and
 *  again on login/logout. Pass `null` on sign-out. */
export function setSentryUser(
  user: {
    id: number;
    email: string;
    tenant?: string | null;
  } | null,
): void {
  if (!CONFIG.SENTRY_DSN) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
  });
  if (user.tenant) Sentry.setTag('tenant', user.tenant);
}

/** Manually report a caught error with an attached `X-Request-ID` and
 *  arbitrary breadcrumb context. Preferred over `throw` when the error
 *  has been handled locally but is still notable for ops. */
export function reportHandled(err: unknown, context?: Record<string, unknown>): void {
  if (!CONFIG.SENTRY_DSN) return;
  Sentry.withScope((scope) => {
    if (context) {
      for (const [k, v] of Object.entries(context)) scope.setExtra(k, v);
    }
    Sentry.captureException(err);
  });
}

function clampFraction(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(1, value));
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const req = event.request;
  if (req?.headers && typeof req.headers === 'object') {
    const h = req.headers as Record<string, string>;
    if (h['Authorization']) h['Authorization'] = 'Bearer [redacted]';
    if (h['Cookie']) h['Cookie'] = '[redacted]';
  }
  return event;
}
